import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {connect} from 'react-redux';
import {Result} from 'antd';
import CodeAuth from '../../../CodeAuth';
import {useIsMount} from '../../../../hooks';
import {companyActions} from '../../../../state/actions/company.actions';
import {userActions} from '../../../../state/actions/user.actions';
import {localStorageKeysConstants} from '../../../../constants';
import {
  StyledSmsAuthModalContainer,
  StyledSmsAuthModalSuccessContainer,
  StyledSmsAuthModal
} from './StyledSmsAuthModal';
import AnimatedSwitcher from '../../../animations/AnimatedSwitcher';
import {scaHelpers, systemHelpers} from '../../../../utils/helpers';
import {firebaseEvents} from '../../../../snippets/firebase';

let interval = null;
const defaultVerificationDetail = {sessionId: null, errors: null};

const logEvent = (status) => systemHelpers.logEvent(firebaseEvents.USER_CODE_AUTH, {status});

const SmsAuthModal = ({
  open,
  handleCancel,
  phoneNumber,
  onSuccess,
  initialSeconds = 59,
  startPhoneVerification,
  endPhoneVerification,
  successWindowDelay = 1000,
  title,
  operationName,
  ...rest
}) => {
  const [t] = useTranslation(['main', 'cards']);
  const [seconds, setSeconds] = useState(initialSeconds);
  const [verificationDetails, setVerificationDetails] = useState(defaultVerificationDetail);
  const [isDisabled, setIsDisabled] = useState(false);
  const [canStartCount, setCanStartCount] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const isMounted = !useIsMount();

  const trans = (key) => t(`cards:modal.2fa.${key}`);

  const finishLoading = () => setIsDisabled(false);

  const handleStartVerification = () => {
    setIsDisabled(true);
    setCanStartCount(false);
    startPhoneVerification({
      data: {
        sca_session_type: scaHelpers.getOperationType(operationName)
      },
      successCallback: (data) => {
        setSeconds(initialSeconds);
        finishLoading();
        setVerificationDetails({...verificationDetails, sessionId: data.session_id, errors: null});
        setCanStartCount(true);
      },
      errorCallback: finishLoading
    });
  }

  useEffect(() => {
    if (isMounted && !open) {
      setSeconds(initialSeconds);
      setVerificationDetails(defaultVerificationDetail);
      setIsCompleted(false);
    }
    if (open) {
      handleStartVerification();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const startTimer = () => {
    interval = setInterval(() => {
      if (seconds > 0) {
        setSeconds(seconds - 1);
      } else {
        clearInterval(interval);
      }
    }, 1000);
  };

  useEffect(() => {
    if (seconds > 0 && open) {
      startTimer();
      return () => {
        if (interval) clearInterval(interval);
      }
    } else if (seconds <= 0) {
      // handleClose && handleClose();
    }
  }, [seconds, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSuccessValidate = (data) => {
    setIsDisabled(true);
    endPhoneVerification(
      {code: data.code, session_id: verificationDetails.sessionId},
      (data) => {
        setIsCompleted(true);
        finishLoading();
        setTimeout(() => {
          localStorage.setItem(localStorageKeysConstants.TWO_FA_STARTED, String(Date.now()));
          onSuccess?.(data.session_key);
        }, successWindowDelay);
        logEvent('success');
      },
      (errors) => {
        setVerificationDetails({...verificationDetails, errors});
        finishLoading();
        logEvent('error');
      }
    );
  }

  const onCodeKeyChange = () => {
    if (verificationDetails.errors) setVerificationDetails({...verificationDetails, errors: null});
  }

  const handleAfterClose = () => {
    if (isCompleted) setIsCompleted(false);
  }

  const items = [
    {
      key: 'form',
      component: (
        <StyledSmsAuthModalContainer>
          <h3>{trans('enterCode')}</h3>
          <p className='text-center'>
            {trans('pleaseTypeCode')}
            <br />
            <b>{phoneNumber}</b>
          </p>
          {open && (
            <CodeAuth
              autofocus={true}
              numberCount={6}
              onSuccess={handleSuccessValidate}
              onResend={handleStartVerification}
              canStartCount={canStartCount}
              additionalErrors={verificationDetails.errors}
              onKeyChange={onCodeKeyChange}
            />
          )}
        </StyledSmsAuthModalContainer>
      ),
    },
    {
      key: 'success',
      component: (
        <StyledSmsAuthModalSuccessContainer>
          <Result
            status='success'
            title={trans('completedSuccessfully')}
          />
        </StyledSmsAuthModalSuccessContainer>
      ),
    }
  ];

  const displayedKey = isCompleted ? 'success' : 'form';

  return (
    <StyledSmsAuthModal
      afterClose={handleAfterClose}
      className={isDisabled && 'disabled'}
      footer={null}
      onCancel={handleCancel}
      open={open}
      title={title || trans('enterCode')}
      zIndex={1001}
      {...rest}
    >
      <AnimatedSwitcher items={items} displayedKey={displayedKey} />
    </StyledSmsAuthModal>
  );
}

SmsAuthModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  initialSeconds: PropTypes.number,
  operationName: PropTypes.string,
  successWindowDelay: PropTypes.number,
  title: PropTypes.string
}

const mapStateToProps = state => {
  const {phone_number: phoneNumber} = state.user.user;
  return {
    phoneNumber
  }
}

const mapDispatchToProps = {
  startPhoneVerification: userActions.startPhoneVerification,
  endPhoneVerification: userActions.endPhoneVerification,
  getCompanyInformation: companyActions.getCompanyInformation
};

export default connect(mapStateToProps, mapDispatchToProps)(SmsAuthModal);
